/* ================================================================== */
/* UNDERTOW — tide · zone                                              */
/* ================================================================== */
/**
 * A whirlpool opens on the circle, and everything loose goes down it.
 *
 * **Two numbers are the ability.** `swirl` and `drain` are the circulation and
 * the sink strength of an ideal vortex-sink, in metres² per second:
 *
 * ```
 *   v_θ(r) = swirl / r          the free vortex — how hard it turns
 *   v_r(r) = −drain / r         the sink        — how hard it pulls in
 * ```
 *
 * Their *ratio* is the whole shape of the cast. A streamline of that field
 * satisfies `dr/dθ = −(drain/swirl)·r`, whose solution is `r = r₀·e^{−bθ}` with
 * `b = drain/swirl` — a **logarithmic spiral**, the one spiral with a constant
 * angle between the streamline and the radius. Turn `swirl` up and the arms
 * wind tighter and the fall takes longer; turn `drain` up and they straighten
 * out into a sink. There is no separate "spiral tightness" slider because there
 * cannot be one: the tightness is `drain/swirl` and inventing a third control
 * for it would let the picture and the physics disagree.
 *
 * `TiderushAbility`'s sibling trick is that the light on the floor is generated
 * by the water. This one's is that **the water, the light and the debris are
 * generated by the same flow field** — `UndertowAbility#flowAt()` is the only
 * place either number is read, and the surface's drift, the caustic net's
 * ripples and the debris' orbit are all it, evaluated somewhere different.
 *
 * **`coreRadius` matters more than it looks.** Both terms go as `1/r`, so
 * without a floor the eye of the vortex is a singularity: infinite speed,
 * infinite spin, and a debris ring that reaches the centre in one frame. It is
 * the radius at which the vortex stops being ideal and starts being a hole, and
 * it is also the radius the debris fades out at.
 *
 * **`poolHeight` is not decoration either.** The brief for this ability is that
 * the debris goes *under* the surface and is seen through it, and a sheet of
 * water sitting five centimetres off a stone floor has five centimetres of
 * under. So the pool stands `poolHeight` metres proud, with its waterline
 * feathered off, and there is genuinely a body of water for things to sink
 * into. It is also exactly the depth the caustics are told the light crosses —
 * one number, both consumers, which is the case where sharing *is* the design.
 *
 * The swell is authored in **mirror pairs** for the reason `tiderush`'s block
 * gives at length: the caustic quad's frame is right-handed and
 * `LiquidSurface`'s is not, so the net is the water's mirror image and only a
 * wave set symmetric about the pool's own +X axis survives that mirror
 * unchanged.
 */
export const undertow = {
  /* --- the cast --- */
  range: 19.0, // how far out the circle can be placed, metres
  minRange: 3.0, // closer than this and the cast is refused
  zoneRadius: 6.5, // radius of the aimed circle, metres — the vortex's rim
  speed: 26.0, // how fast the cast travels out to the circle, metres/second
  pullTime: 2.8, // seconds the vortex holds open and pulls
  closeTime: 1.5, // seconds it takes to shut
  cooldown: 1.3,
  castAnim: 'cast2', // which clip in `CAST_ANIMATIONS` the body throws

  /* --- THE FLOW FIELD -------------------------------------------------- */
  // Read in exactly one place: `UndertowAbility#flowAt(radius, out)`.
  swirl: 9.0, // m²/s of circulation.  v_θ = swirl / r
  drain: 2.2, // m²/s of sink.        v_r = −drain / r
  coreRadius: 0.55, // metres — where both are clamped and the eye begins
  spinUp: 0.3, // 0..1 of the pull the field takes to reach full strength
  flowSampleAt: 0.45, // 0..1 of the rim — the radius the surface's one drift vector is taken at
  eddyShare: 0.45, // how much of the tangential flow is handed to the surface's curl term
  radialFall: 6.0, // metres to 1/e of the surface's own radial term

  /* --- the pool -------------------------------------------------------- */
  poolHeight: 0.7, // metres the mean plane stands above the floor
  poolFill: 1.0, // 0..1 of the half-extent the water reaches
  poolRound: 1.0, // 0 rectangular footprint, 1 elliptical — a pool is round
  poolEdge: 0.2, // 0..1 of the field over which the waterline fades out
  poolRagged: 0.35, // 0..1 how ragged that line is
  poolRaggedScale: 0.9, // cycles per metre of the raggedness
  poolOpacity: 1.0,
  contactFade: 0.25, // metres of soft fade where the water meets standing geometry
  openCurve: 1.6, // exponent on how the pool grows out of the impact point

  /* --- the swell: four directional waves, in mirror pairs -------------- */
  swellAmpA: 0.085, // metres
  swellAmpB: 0.085, //  pair of A
  swellAmpC: 0.045,
  swellAmpD: 0.045, //  pair of C
  swellLengthA: 4.4, // metres, crest to crest
  swellLengthB: 4.4,
  swellLengthC: 2.0,
  swellLengthD: 2.0,
  swellSpeedA: 1.5, // metres/second
  swellSpeedB: 1.5,
  swellSpeedC: 1.0,
  swellSpeedD: 1.0,
  swellAngleA: 0.55, // radians in the pool's frame
  swellAngleB: -0.55, //  the mirror of A
  swellAngleC: 1.42,
  swellAngleD: -1.42, //  the mirror of C
  steepness: 0.4, // 0 sine, 1 Gerstner cusps

  /* --- chop and grain -------------------------------------------------- */
  chop: 0.05, // metres of high-frequency displacement
  chopScale: 1.6, // cycles per metre
  chopSpeed: 0.6, // metres/second the chop field drifts
  detail: 0.014, // metres — fragment-only; lives entirely in the normal
  detailScale: 7.6, // cycles per metre
  detailSpeed: 1.05,

  /* --- ripples (something going under dimples the surface) -------------- */
  // `rippleAmp` is NEGATIVE on purpose: a packet posted where the vortex takes
  // something down is a dimple that rings outward, not a splash.
  rippleAmp: -0.13, // metres at strength 1
  rippleSpeed: 2.6, // metres/second the ring travels out
  rippleLength: 0.95, // metres, crest to crest inside the packet
  rippleWidth: 0.7, // metres of the gaussian envelope
  rippleDecay: 1.0, // seconds to 1/e
  rippleSpread: 2.8, // metres over which it also thins with radius
  rippleRate: 3.2, // packets per second posted under the debris

  /* --- foam ------------------------------------------------------------ */
  // Seeded by surface speed, which the flow field supplies — so the froth ends
  // up where the vortex is fastest, which is a ring around the eye. Nothing
  // draws that ring; it is where `1/r` gets large.
  foam: 0.9, // 0..1 master
  foamScale: 6.0, // cycles per metre of the speckle
  foamSharp: 1.2,
  foamCrest: 0.4, // a pool has no lip, so this is nearly off
  foamSpeed: 1.1, // how much surface speed seeds it — the important one here
  foamGateLow: 0.6, // m/s below which the surface is calm and bare
  foamGateHigh: 3.2, // m/s above which it is fully frothed

  /* --- shading --------------------------------------------------------- */
  poolDepth: 0.7, // metres of water under the mean plane, for the depth tint
  depthTint: 1.15, // Beer-Lambert density in the body, per metre
  translucency: 0.5, // a pool has no folded lip; kept low deliberately
  ambient: 0.32,
  specular: 1.5,
  shininess: 78, // Blinn-Phong exponent
  fresnel: 1.35, // high: a pool is read almost entirely by what it reflects
  envIntensity: 0.9,
  skyIntensity: 0.6,
  glow: 1.0,
  normalEps: 0.04, // metres — the finite-difference step of the shading normal
  colorDeep: '#04202c', // the body
  colorShallow: '#2b93a8', // thin water at the rim
  colorFoam: '#e6faff', // froth
  colorSpec: '#ffffff', // highlight
  colorSky: '#33596f', // the sky fallback under the reflection

  /* --- the caustic net -------------------------------------------------- */
  // Bound to the pool's own uniform boxes. `depth` is `poolHeight` plus the
  // bias below — the light crosses exactly the water that is standing there.
  netHeight: 0.014, // metres the quad floats above the floor
  netDepthBias: 0.25, // metres added to `poolHeight` for the fold's lever arm
  netIor: 1.335, // water. The shader is handed 1 − 1/ior
  netDispersion: 0.08, // 0..1 how far the red and blue folds sit either side of green
  netStep: 0.07, // metres between the six Hessian taps
  netAbsorb: 0.28, // 1/metres down the water column
  netFoldFloor: 0.2, // keeps 1/|det| finite; also the widest a filament gets
  netThreshold: 1.05, // compression below this is flat water, and black
  netGain: 0.6,
  netSharp: 1.35, // exponent on the surviving compression
  netRolloff: 0.2, // soft clip, so a fold does not detonate the bloom
  netPenumbra: 0.36, // 0..1 of the reach over which the pool of light dies
  netReach: 1.05, // × the rim radius. Past ~1.1 the net throws light onto dry floor
  netWash: 0.16, // the general lit-pool light between the filaments
  netFringeAt: 1.5, // where on the fold the colour hands over to the fringe
  netEmissive: 1.45,
  netOpacity: 1.0,
  netDepthFade: 0.4, // metres of soft fade against anything standing on the floor
  colorNet: '#9fe8ff', // the filaments
  colorFringe: '#ffffff', // the very top of a fold
  colorWash: '#175a72', // the general light in the pool

  /* --- the debris ------------------------------------------------------- */
  // A flock on the vortex's own streamline: the lead's angle and radius are
  // `flowAt` integrated, so dragging `swirl` re-winds the whole raft.
  debrisCount: 176, // agents
  debrisSize: 0.24, // metres, nose to tail
  debrisAspect: 1.35, // span / length
  debrisSizeJitter: 0.5, // ±fraction
  debrisLatticeX: 5, // cells across
  debrisLatticeY: 3, // cells up — this is the depth spread inside the water
  debrisLatticeZ: 12, // ranks strung out behind the lead, in TIME
  debrisSpacingSide: 0.62, // metres between lateral cells
  debrisSpacingUp: 0.24, // metres between vertical cells
  debrisLag: 0.85, // seconds the back rank trails the lead by — the arm's length
  debrisJitter: 0.2, // metres of slop off the cell
  debrisChurn: 0.5, // radians/second the formation rolls
  debrisBreathe: 0.12, // fraction it swells by
  debrisBreatheRate: 1.1, // radians/second
  debrisWander: 0.12, // metres of curl drift
  debrisWanderScale: 0.55, // features per metre
  debrisWanderSpeed: 0.5,
  debrisGather: 1.0, // 0 collapses every agent onto the streamline itself
  debrisSinkRim: 0.22, // metres below the mean plane at the rim
  debrisSinkCore: 0.62, // metres below it at the eye — this is the "pulled under"
  debrisBank: 0.05, // radians of roll per m/s² of lateral acceleration
  debrisBankMax: 1.0, // radians
  debrisCurl: 0.3, // curl across the chord, fraction of size
  debrisBillboard: 0.2, // 0 agent frame, 1 camera facing
  debrisEdgeStretch: 1.7, // how much an edge-on card grows
  debrisEdgeGain: 1.8, // emission multiplier when edge-on
  debrisFadeIn: 0.14, // 0..1 of the fall over which new debris appears at the rim
  debrisFadeOut: 0.26, // 0..1 of it over which it is swallowed at the eye
  debrisRevealSpread: 0.5, // width of that wave — how staggered the vanishing is
  debrisLit: 0.85, // 0 emissive, 1 wrapped diffuse
  debrisTint: 0.3, // where in the gradient the raft sits
  debrisTintJitter: 0.3, // ±per-agent walk along it
  debrisTintAlong: 0.35, // extra walk from head to tail
  debrisOpacity: 1.0,
  debrisGlow: 0.5,
  debrisSoftFade: 0.3, // metres of depth feather against solid geometry
  colorDebrisA: '#8fb6bd',
  colorDebrisB: '#4d7c88',
  colorDebrisC: '#27505c',
  colorDebrisD: '#0c2028',

  /* --- spume: the froth thrown off the surface, carried by the flow ----- */
  spumeRate: 130, // flecks/second
  spumeSize: 0.06, // metres
  spumeSpeed: 0.8, // metres/second ON TOP of the flow velocity it inherits
  spumeLifetime: 1.3, // seconds
  spumeGravity: -3.5, // metres/second²
  spumeAt: 0.7, // 0..1 of the rim the flecks are born at
  colorSpumeA: '#f0ffff',
  colorSpumeB: '#a9e2ee',
  colorSpumeC: '#4e8fa2',
  colorSpumeD: '#12333f',

  /* --- the haze standing over the eye ----------------------------------- */
  mistRate: 40, // puffs/second
  mistSize: 0.9, // metres
  mistSpeed: 1.2, // metres/second
  mistLifetime: 2.4, // seconds
  mistRise: 0.6, // metres/second²
  mistOpacity: 0.45,
  mistTurbulence: 0.9,
  colorMistA: '#cfeaf2',
  colorMistB: '#93bcc8',
  colorMistC: '#547c8a',
  colorMistD: '#1e3038',

  /* --- grit: the fine stuff too small to be a debris agent -------------- */
  gritRate: 90, // chips/second
  gritSize: 0.05, // metres
  gritSpeed: 0.6, // metres/second on top of the flow it inherits
  gritLifetime: 1.6, // seconds
  gritGravity: -2.0, // metres/second²
  colorGritA: '#a9c4c9',
  colorGritB: '#63838c',
  colorGritC: '#2f4c56',
  colorGritD: '#0a181e',

  /* --- what it leaves on the floor --------------------------------------- */
  rimRate: 7.0, // marks/second laid around the waterline
  rimRadius: 1.6, // metres
  rimLife: 5.0, // seconds
  rimIntensity: 0.9,
  colorRimA: '#0c2731', // soaked stone
  colorRimB: '#b7e4ef', // the drying line

  /* --- opening and closing ------------------------------------------------ */
  burstSize: 3.0, // metres — the gulp as it shuts
  burstIntensity: 1.4,
  burstSpume: 200, // flecks thrown out of the close
  burstGrit: 140,
  burstRipples: 3, // ripple packets posted at the close
  shockRadius: 5.0, // metres
  openShake: 0.35,
  shakeDuration: 0.7, // seconds
  openFlash: 0.07, // 0..1 screen flash
  rumble: 0.075, // per-second shake while the vortex holds
  colorBurstA: '#e6fbff',
  colorBurstB: '#4fb6d0',
  colorBurstC: '#0c3a4c',
  colorShockA: '#d2f4ff',
  colorShockB: '#31a0bd',
  colorFlash: '#8fdcf0',

  /* --- dynamic light ------------------------------------------------------- */
  lightIntensity: 12.0,
  lightRadius: 11.0,
  lightHeight: 0.6, // metres above the pool the light sits
  lightColor: '#6fdcf2'
};

/** Editor layout. Folder order is the order they are built in. */
export const undertowSchema = {
  'The cast': [
    'range',
    'minRange',
    ['zoneRadius', 1, 16, 0.1, 'rim radius'],
    'speed',
    ['pullTime', 0.2, 8, 0.05, 'pull time'],
    ['closeTime', 0.2, 6, 0.05, 'close time'],
    'cooldown',
    'castAnim'
  ],
  'The flow field': [
    ['swirl', 0, 40, 0.1, 'swirl (m²/s)'],
    ['drain', 0.05, 20, 0.05, 'drain (m²/s)'],
    ['coreRadius', 0.05, 4, 0.01, 'core radius'],
    ['spinUp', 0.02, 1, 0.01, 'spin-up'],
    ['flowSampleAt', 0.05, 1, 0.01, 'drift sampled at'],
    ['eddyShare', 0, 2, 0.01, 'curl share'],
    ['radialFall', 0.5, 20, 0.1, 'radial falloff']
  ],
  'The pool': [
    ['poolHeight', 0, 3, 0.01, 'height off the floor'],
    ['poolFill', 0, 1, 0.01, 'fill'],
    ['poolRound', 0, 1, 0.01, 'rectangular → elliptical'],
    ['poolEdge', 0.01, 1, 0.01, 'waterline feather'],
    ['poolRagged', 0, 1, 0.01, 'waterline raggedness'],
    ['poolRaggedScale', 0.05, 4, 0.01, 'raggedness scale'],
    ['poolOpacity', 0, 1, 0.01, 'opacity'],
    ['contactFade', 0.02, 2, 0.01, 'soft intersection'],
    ['openCurve', 0.2, 4, 0.01, 'opening curve']
  ],
  'The swell (mirror pairs)': [
    ['swellAmpA', 0, 1, 0.005, 'amp A'],
    ['swellAmpB', 0, 1, 0.005, 'amp B — pairs A'],
    ['swellAmpC', 0, 1, 0.005, 'amp C'],
    ['swellAmpD', 0, 1, 0.005, 'amp D — pairs C'],
    ['swellLengthA', 0.2, 20, 0.05, 'length A'],
    ['swellLengthB', 0.2, 20, 0.05, 'length B'],
    ['swellLengthC', 0.2, 20, 0.05, 'length C'],
    ['swellLengthD', 0.2, 20, 0.05, 'length D'],
    ['swellSpeedA', 0, 8, 0.05, 'speed A'],
    ['swellSpeedB', 0, 8, 0.05, 'speed B'],
    ['swellSpeedC', 0, 8, 0.05, 'speed C'],
    ['swellSpeedD', 0, 8, 0.05, 'speed D'],
    ['swellAngleA', -3.2, 3.2, 0.01, 'bearing A'],
    ['swellAngleB', -3.2, 3.2, 0.01, 'bearing B'],
    ['swellAngleC', -3.2, 3.2, 0.01, 'bearing C'],
    ['swellAngleD', -3.2, 3.2, 0.01, 'bearing D'],
    ['steepness', 0, 1.2, 0.01, 'Gerstner cusps'],
    ['chop', 0, 0.4, 0.001, 'chop'],
    ['chopScale', 0.05, 6, 0.01, 'chop scale'],
    ['chopSpeed', 0, 4, 0.01, 'chop drift'],
    ['detail', 0, 0.1, 0.001, 'normal grain'],
    ['detailScale', 0.5, 20, 0.1, 'grain scale'],
    ['detailSpeed', 0, 4, 0.01, 'grain drift']
  ],
  'Ripples & foam': [
    ['rippleAmp', -1, 1, 0.005, 'amplitude (negative = dimple)'],
    ['rippleSpeed', 0.1, 12, 0.05, 'front speed'],
    ['rippleLength', 0.1, 4, 0.01, 'wavelength'],
    ['rippleWidth', 0.05, 3, 0.01, 'packet width'],
    ['rippleDecay', 0.05, 5, 0.01, 'decay (s)'],
    ['rippleSpread', 0.1, 10, 0.05, 'radial thinning'],
    ['rippleRate', 0, 20, 0.1, 'packets / second'],
    ['foam', 0, 1, 0.01, 'foam'],
    ['foamScale', 0.5, 16, 0.05, 'foam speckle'],
    ['foamSharp', 0.2, 4, 0.01, 'foam sharpness'],
    ['foamCrest', 0, 3, 0.01, 'seeded by height'],
    ['foamSpeed', 0, 3, 0.01, 'seeded by speed'],
    ['foamGateLow', 0, 6, 0.01, 'calm below (m/s)'],
    ['foamGateHigh', 0.1, 10, 0.05, 'frothed above (m/s)']
  ],
  'Shading': [
    ['poolDepth', 0, 3, 0.01, 'depth under the plane'],
    ['depthTint', 0, 10, 0.01, 'absorption / metre'],
    ['translucency', 0, 4, 0.01, 'backlight'],
    ['ambient', 0, 1, 0.01, 'ambient floor'],
    ['specular', 0, 6, 0.01, 'specular'],
    ['shininess', 4, 256, 1, 'gloss'],
    ['fresnel', 0, 3, 0.01, 'fresnel'],
    ['envIntensity', 0, 3, 0.01, 'reflection'],
    ['skyIntensity', 0, 3, 0.01, 'sky fallback'],
    ['glow', 0, 4, 0.01, 'glow'],
    ['normalEps', 0.005, 0.3, 0.001, 'normal step'],
    ['colorDeep', 'body'],
    ['colorShallow', 'thin water'],
    ['colorFoam', 'froth'],
    ['colorSpec', 'highlight'],
    ['colorSky', 'sky fallback']
  ],
  'The caustic net': [
    ['netHeight', 0.002, 0.2, 0.001, 'height off the floor'],
    ['netDepthBias', -1, 3, 0.01, 'depth over poolHeight'],
    ['netIor', 1.01, 2, 0.001, 'index of refraction'],
    ['netDispersion', 0, 0.4, 0.005, 'chromatic spread'],
    ['netStep', 0.02, 0.4, 0.005, 'Hessian tap step'],
    ['netAbsorb', 0, 2, 0.01, 'absorption / metre'],
    ['netFoldFloor', 0.01, 1, 0.005, 'fold floor'],
    ['netThreshold', 0, 4, 0.01, 'black-water threshold'],
    ['netGain', 0, 3, 0.01, 'gain'],
    ['netSharp', 0.1, 4, 0.01, 'sharpness'],
    ['netRolloff', 0, 2, 0.01, 'soft clip'],
    ['netPenumbra', 0.02, 1, 0.01, 'edge penumbra'],
    ['netReach', 0.2, 3, 0.01, 'reach past the rim'],
    ['netWash', 0, 1, 0.005, 'lit pool between filaments'],
    ['netFringeAt', 0, 6, 0.01, 'fringe hand-over'],
    ['netEmissive', 0, 5, 0.01, 'emissive'],
    ['netOpacity', 0, 1, 0.01, 'opacity'],
    ['netDepthFade', 0.02, 3, 0.01, 'soft intersection'],
    ['colorNet', 'filaments'],
    ['colorFringe', 'fold crest'],
    ['colorWash', 'pool light']
  ],
  'The debris': [
    ['debrisCount', 0, 320, 1, 'agents'],
    ['debrisSize', 0.02, 1.2, 0.005, 'size'],
    ['debrisAspect', 0.2, 4, 0.01, 'aspect'],
    ['debrisSizeJitter', 0, 1, 0.01, 'size jitter'],
    ['debrisSinkRim', -1, 3, 0.01, 'depth at the rim'],
    ['debrisSinkCore', -1, 4, 0.01, 'depth at the eye'],
    ['debrisLatticeX', 1, 16, 1, 'cells across'],
    ['debrisLatticeY', 1, 12, 1, 'cells deep'],
    ['debrisLatticeZ', 1, 24, 1, 'ranks behind'],
    ['debrisSpacingSide', 0.02, 2, 0.01, 'lateral spacing'],
    ['debrisSpacingUp', 0.02, 2, 0.01, 'vertical spacing'],
    ['debrisLag', 0.02, 3, 0.01, 'arm length (s)'],
    ['debrisJitter', 0, 1, 0.01, 'cell slop'],
    ['debrisChurn', -4, 4, 0.01, 'formation roll'],
    ['debrisBreathe', 0, 1, 0.01, 'swell'],
    ['debrisBreatheRate', 0, 8, 0.01, 'swell rate'],
    ['debrisWander', 0, 1, 0.01, 'curl drift'],
    ['debrisWanderScale', 0.05, 4, 0.01, 'drift scale'],
    ['debrisWanderSpeed', 0, 4, 0.01, 'drift speed'],
    ['debrisGather', 0, 1, 0.01, 'gather onto the streamline'],
    ['debrisBank', 0, 0.5, 0.005, 'bank'],
    ['debrisBankMax', 0, 3, 0.01, 'bank limit'],
    ['debrisCurl', 0, 1, 0.01, 'chip curl'],
    ['debrisBillboard', 0, 1, 0.01, 'camera facing'],
    ['debrisEdgeStretch', 1, 4, 0.01, 'edge-on stretch'],
    ['debrisEdgeGain', 0, 6, 0.01, 'edge-on gain'],
    ['debrisFadeIn', 0.01, 1, 0.01, 'appears over'],
    ['debrisFadeOut', 0.01, 1, 0.01, 'swallowed over'],
    ['debrisRevealSpread', 0.02, 1, 0.01, 'reveal width'],
    ['debrisLit', 0, 1, 0.01, 'lit vs emissive'],
    ['debrisTint', 0, 1, 0.01, 'gradient position'],
    ['debrisTintJitter', 0, 1, 0.01, 'gradient jitter'],
    ['debrisTintAlong', 0, 1, 0.01, 'gradient along'],
    ['debrisOpacity', 0, 1, 0.01, 'opacity'],
    ['debrisGlow', 0, 4, 0.01, 'glow'],
    ['debrisSoftFade', 0.02, 2, 0.01, 'soft intersection'],
    ['colorDebris*', 'Debris colour']
  ],
  'Spume, mist & grit': [
    ['spumeRate', 0, 600, 1, 'spume rate'],
    ['spumeSize', 0.005, 0.5, 0.005, 'spume size'],
    ['spumeSpeed', 0, 12, 0.05, 'spume speed'],
    ['spumeLifetime', 0.05, 4, 0.01, 'spume lifetime'],
    ['spumeGravity', -30, 5, 0.1, 'spume gravity'],
    ['spumeAt', 0.05, 1.4, 0.01, 'born at (× rim)'],
    ['mistRate', 0, 400, 1, 'mist rate'],
    ['mistSize', 0.05, 4, 0.01, 'mist size'],
    ['mistSpeed', 0, 8, 0.05, 'mist speed'],
    ['mistLifetime', 0.2, 8, 0.05, 'mist lifetime'],
    ['mistRise', -2, 4, 0.01, 'mist rise'],
    ['mistOpacity', 0, 1, 0.005, 'mist opacity'],
    ['mistTurbulence', 0, 3, 0.01, 'mist turbulence'],
    ['gritRate', 0, 400, 1, 'grit rate'],
    ['gritSize', 0.005, 0.4, 0.005, 'grit size'],
    ['gritSpeed', 0, 12, 0.05, 'grit speed'],
    ['gritLifetime', 0.05, 4, 0.01, 'grit lifetime'],
    ['gritGravity', -30, 5, 0.1, 'grit gravity'],
    ['colorSpume*', 'Spume colour'],
    ['colorMist*', 'Mist colour'],
    ['colorGrit*', 'Grit colour']
  ],
  'The waterline': [
    ['rimRate', 0, 30, 0.1, 'marks / second'],
    ['rimRadius', 0.2, 8, 0.05, 'mark radius'],
    ['rimLife', 0.5, 20, 0.1, 'mark lifetime'],
    ['rimIntensity', 0, 3, 0.01, 'mark intensity'],
    ['colorRimA', 'soaked stone'],
    ['colorRimB', 'drying line']
  ],
  'Opening & closing': [
    ['burstSize', 0.2, 12, 0.05, 'burst size'],
    ['burstIntensity', 0, 5, 0.01, 'burst intensity'],
    ['burstSpume', 0, 800, 1, 'burst spume'],
    ['burstGrit', 0, 600, 1, 'burst grit'],
    ['burstRipples', 0, 8, 1, 'ripple packets'],
    ['shockRadius', 0.5, 20, 0.1, 'shockwave radius'],
    ['openShake', 0, 3, 0.01, 'shake'],
    ['shakeDuration', 0.1, 4, 0.01, 'shake duration'],
    ['openFlash', 0, 1, 0.005, 'screen flash'],
    ['rumble', 0, 0.5, 0.005, 'hold rumble'],
    ['colorBurstA', 'burst shell'],
    ['colorBurstB', 'burst body'],
    ['colorBurstC', 'burst crest'],
    ['colorShockA', 'shockwave ring'],
    ['colorShockB', 'shockwave crest'],
    ['colorFlash', 'screen flash colour']
  ],
  'Dynamic light': [
    ['lightIntensity', 0, 60, 0.5, 'light intensity'],
    ['lightRadius', 0.5, 40, 0.1, 'light radius'],
    ['lightHeight', -1, 4, 0.01, 'light height'],
    ['lightColor', 'light colour']
  ]
};
